import { skipToken } from "@reduxjs/toolkit/query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Loader from "../../../components/ui/Loader";
import { useGetProfileQuery } from "../../../store/api/authApi";
import type { Lease } from "../../../store/api/leasesApi";
import { useGetPropertyQuery } from "../../../store/api/propertiesApi";
import UploadSignedAgreementModal from "./UploadSignedAgreementModal";

interface LeaseDetailsProps {
	lease: Lease | null | undefined;
}

const LeaseDetails: React.FC<LeaseDetailsProps> = ({ lease }) => {
	const navigate = useNavigate();
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

	// Fetch property details
	const { data: propertyData, isLoading: isLoadingProperty } =
		useGetPropertyQuery(lease?.property_id || skipToken);

	// Fetch user profile for emergency contacts
	const { data: userProfile } = useGetProfileQuery();

	const formatCurrency = (amount: number | undefined) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount || 0);
	};

	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
		});
	};

	const getDaysUntilExpiry = (endDateString: string | undefined) => {
		if (!endDateString) return 0;
		const endDate = new Date(endDateString);
		const today = new Date();
		// Reset time to midnight for accurate day calculation
		today.setHours(0, 0, 0, 0);
		endDate.setHours(0, 0, 0, 0);
		if (isNaN(endDate.getTime())) return 0;
		const diffTime = endDate.getTime() - today.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const daysUntilExpiry = getDaysUntilExpiry(lease?.end_date);

	// Get lease documents from lease.documents array
	const leaseDocuments = lease?.documents
		? lease.documents.map((url, index) => {
				const urlParts = url.split("/");
				const fileName =
					urlParts[urlParts.length - 1] || `document-${index + 1}`;
				const extension = fileName.split(".").pop()?.toUpperCase() || "PDF";
				// Extract file name without extension
				const name = fileName
					.replace(/\.[^/.]+$/, "")
					.replace(/[_-]/g, " ")
					.replace(/\b\w/g, (l) => l.toUpperCase());

				return {
					id: index + 1,
					name: name || `Lease Document ${index + 1}`,
					type: extension,
					url: url,
					uploadDate: lease.created_at || lease.updated_at,
				};
		  })
		: [];

	const getDocumentIcon = (type: string) => {
		switch (type.toLowerCase()) {
			case "pdf":
				return "FileText";
			case "doc":
			case "docx":
				return "FileText";
			case "jpg":
			case "jpeg":
			case "png":
				return "Image";
			default:
				return "File";
		}
	};

	const handleDownload = (documentUrl?: string) => {
		if (documentUrl) {
			// Download actual document from URL
			window.open(documentUrl, "_blank");
			return;
		}

		// Fallback: Generate text document if no documents available
		if (!lease) return;

		const leaseDoc = `
LEASE AGREEMENT

Lease ID: ${lease.lease_code || lease.id || "N/A"}
Property Address: ${lease.property_address || propertyData?.address || "N/A"}

TERMS:
- Start Date: ${
			lease.start_date
				? new Date(lease.start_date).toLocaleDateString("en-NG")
				: "N/A"
		}
- End Date: ${
			lease.end_date
				? new Date(lease.end_date).toLocaleDateString("en-NG")
				: "N/A"
		}
- Monthly Rent: ${formatCurrency(lease.monthly_rent)}
- Security Deposit: ${formatCurrency(lease.security_deposit)}
- Status: ${lease.status?.toUpperCase() || "N/A"}

Generated on: ${new Date().toLocaleDateString("en-NG")}
		`.trim();

		const blob = new Blob([leaseDoc], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `lease-${lease.lease_code || lease.id || "document"}-${
			new Date().toISOString().split("T")[0]
		}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	if (!lease) {
		return (
			<div className="text-center py-8 text-muted-foreground">
				<p>No lease information available</p>
			</div>
		);
	}

	if (isLoadingProperty) {
		return (
			<Loader
				size="md"
				showText
				text="Loading property details..."
				className="py-12"
			/>
		);
	}

	return (
		<div className="space-y-6">
			{/* Lease Overview */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Lease Agreement
							</h2>
							<p className="text-muted-foreground mt-1">
								{lease?.property_address}
							</p>
						</div>

						<div className="flex items-center space-x-3">
							<Button
								variant="outline"
								className="flex items-center space-x-2"
								onClick={() => handleDownload()}
							>
								<Icon name="Download" size={16} />
								<span>Download</span>
							</Button>

							<Button
								variant="outline"
								className="flex items-center space-x-2"
								onClick={() => window.print()}
							>
								<Icon name="Printer" size={16} />
								<span>Print</span>
							</Button>
						</div>
					</div>
				</div>

				<div className="p-6">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						{/* Basic Information */}
						<div className="space-y-4">
							<h3 className="font-semibold text-foreground mb-4">
								Basic Information
							</h3>

							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Lease ID:</span>
									<span className="font-medium text-foreground">
										{lease?.lease_code || lease?.id || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">Status:</span>
									<span
										className={`px-2 py-1 text-xs font-medium rounded-full border ${
											lease?.status === "active"
												? "bg-success/20 text-success border-success/30"
												: "bg-muted/30 text-foreground border-border"
										}`}
									>
										{lease?.status?.charAt(0).toUpperCase() +
											lease?.status?.slice(1)}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">Start Date:</span>
									<span className="font-medium text-foreground">
										{formatDate(lease?.start_date)}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">End Date:</span>
									<span className="font-medium text-foreground">
										{formatDate(lease?.end_date)}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">Days Remaining:</span>
									<span
										className={`font-medium ${
											daysUntilExpiry <= 90 ? "text-warning" : "text-foreground"
										}`}
									>
										{daysUntilExpiry} days
									</span>
								</div>
							</div>
						</div>

						{/* Financial Information */}
						<div className="space-y-4">
							<h3 className="font-semibold text-foreground mb-4">
								Financial Details
							</h3>

							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Monthly Rent:</span>
									<span className="font-bold text-foreground text-lg">
										{formatCurrency(lease?.monthly_rent || 0)}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">
										Security Deposit:
									</span>
									<span className="font-medium text-foreground">
										{formatCurrency(lease?.security_deposit || 0)}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="text-muted-foreground">
										Total Lease Value:
									</span>
									<span className="font-medium text-foreground">
										{formatCurrency((lease?.monthly_rent || 0) * 12)}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Renewal Information */}
			{lease?.renewal_deadline && daysUntilExpiry <= 120 && (
				<div className="bg-card border border-warning rounded-lg card-shadow">
					<div className="p-6">
						<div className="flex items-start space-x-4">
							<div className="p-3 bg-warning/10 rounded-lg">
								<Icon name="Clock" size={24} className="text-warning" />
							</div>

							<div className="flex-1">
								<h3 className="font-semibold text-foreground mb-2">
									Lease Renewal Available
								</h3>
								<p className="text-muted-foreground mb-4">
									Your lease expires in {daysUntilExpiry} days. You're eligible
									for renewal. Please contact property management to discuss
									your renewal options and terms.
								</p>

								<div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
									<Button
										className="flex items-center space-x-2"
										onClick={() => navigate("/messages")}
									>
										<Icon name="MessageCircle" size={16} />
										<span>Contact Management</span>
									</Button>

									<Button
										variant="outline"
										className="flex items-center space-x-2"
									>
										<Icon name="Download" size={16} />
										<span>Renewal Application</span>
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Property Details */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<h3 className="font-semibold text-foreground">Property Details</h3>
				</div>

				<div className="p-6">
					{propertyData ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							<div>
								<h4 className="font-medium text-foreground mb-3">
									Property Features
								</h4>
								<div className="space-y-2 text-sm">
									{propertyData.bedrooms && propertyData.bathrooms && (
										<div className="flex items-center space-x-2">
											<Icon
												name="Home"
												size={16}
												className="text-muted-foreground"
											/>
											<span className="text-foreground">
												{propertyData.bedrooms} Bedroom
												{propertyData.bedrooms !== 1 ? "s" : ""},{" "}
												{Number(propertyData.bathrooms)} Bathroom
												{Number(propertyData.bathrooms) !== 1 ? "s" : ""}
											</span>
										</div>
									)}
									{propertyData.square_feet && (
										<div className="flex items-center space-x-2">
											<Icon
												name="Square"
												size={16}
												className="text-muted-foreground"
											/>
											<span className="text-foreground">
												{propertyData.square_feet.toLocaleString()} sq ft
											</span>
										</div>
									)}
									{propertyData.features && propertyData.features.length > 0 ? (
										propertyData.features.slice(0, 2).map((feature, idx) => (
											<div key={idx} className="flex items-center space-x-2">
												<Icon
													name="CheckCircle"
													size={16}
													className="text-muted-foreground"
												/>
												<span className="text-foreground">{feature}</span>
											</div>
										))
									) : (
										<div className="text-muted-foreground text-xs">
											No features listed
										</div>
									)}
								</div>
							</div>

							{propertyData.features && propertyData.features.length > 2 && (
								<div>
									<h4 className="font-medium text-foreground mb-3">
										Additional Features
									</h4>
									<div className="space-y-2 text-sm">
										{propertyData.features.slice(2, 6).map((feature, idx) => (
											<div key={idx} className="flex items-center space-x-2">
												<Icon
													name="CheckCircle"
													size={16}
													className="text-muted-foreground"
												/>
												<span className="text-foreground">{feature}</span>
											</div>
										))}
									</div>
								</div>
							)}

							{propertyData.features && propertyData.features.length > 6 && (
								<div>
									<h4 className="font-medium text-foreground mb-3">
										More Features
									</h4>
									<div className="space-y-2 text-sm">
										{propertyData.features.slice(6).map((feature, idx) => (
											<div key={idx} className="flex items-center space-x-2">
												<Icon
													name="CheckCircle"
													size={16}
													className="text-muted-foreground"
												/>
												<span className="text-foreground">{feature}</span>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					) : (
						<div className="text-center py-4 text-muted-foreground">
							<p>Property details not available</p>
						</div>
					)}
				</div>
			</div>

			{/* Documents */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h3 className="font-semibold text-foreground">Lease Documents</h3>
							<p className="text-muted-foreground text-sm mt-1">
								Download and view your lease-related documents
							</p>
						</div>
						{!lease?.signed_agreement_url && leaseDocuments.length > 0 && (
							<Button
								variant="default"
								size="sm"
								iconName="Upload"
								iconPosition="left"
								onClick={() => setIsUploadModalOpen(true)}
							>
								Upload Signed Agreement
							</Button>
						)}
						{lease?.signed_agreement_url && (
							<div className="flex items-center space-x-2 text-success">
								<Icon name="CheckCircle" size={16} />
								<span className="text-sm font-medium">Agreement Signed</span>
							</div>
						)}
					</div>
				</div>

				<div className="p-6">
					{leaseDocuments.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{leaseDocuments.map((document) => (
								<div
									key={document.id}
									className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/20 smooth-transition"
								>
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-primary/10 rounded">
											<Icon
												name={getDocumentIcon(document.type)}
												size={20}
												className="text-primary"
											/>
										</div>
										<div>
											<h4 className="font-medium text-foreground">
												{document.name}
											</h4>
											<p className="text-sm text-muted-foreground">
												{document.type.toUpperCase()} •{" "}
												{formatDate(document.uploadDate)}
											</p>
										</div>
									</div>

									<div className="flex items-center space-x-2">
										<Button
											variant="ghost"
											size="icon-sm"
											className="text-muted-foreground hover:text-foreground"
											onClick={(e) => {
												e.preventDefault();
												handleDownload(document.url);
											}}
										>
											<Icon name="Eye" size={16} />
										</Button>

										<Button
											variant="ghost"
											size="icon-sm"
											className="text-muted-foreground hover:text-foreground"
											onClick={(e) => {
												e.preventDefault();
												handleDownload(document.url);
											}}
										>
											<Icon name="Download" size={16} />
										</Button>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-8">
							<Icon
								name="FileText"
								size={48}
								className="mx-auto mb-4 text-muted-foreground opacity-50"
							/>
							<h4 className="font-medium text-foreground mb-2">
								No lease documents
							</h4>
							<p className="text-muted-foreground">
								No documents have been uploaded for this lease yet.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Emergency Contacts */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-6 border-b border-border">
					<h3 className="font-semibold text-foreground">Emergency Contacts</h3>
				</div>

				<div className="p-6">
					{userProfile ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-4">
								<div>
									<h4 className="font-medium text-foreground mb-2">
										Property Management
									</h4>
									<div className="space-y-1 text-sm">
										{userProfile.phone && (
											<div className="flex items-center space-x-2">
												<Icon
													name="Phone"
													size={14}
													className="text-muted-foreground"
												/>
												<a
													href={`tel:${userProfile.phone}`}
													className="text-foreground hover:text-primary"
												>
													{userProfile.phone}
												</a>
											</div>
										)}
										{userProfile.email && (
											<div className="flex items-center space-x-2">
												<Icon
													name="Mail"
													size={14}
													className="text-muted-foreground"
												/>
												<a
													href={`mailto:${userProfile.email}`}
													className="text-foreground hover:text-primary"
												>
													{userProfile.email}
												</a>
											</div>
										)}
										<div className="flex items-center space-x-2">
											<Icon
												name="Clock"
												size={14}
												className="text-muted-foreground"
											/>
											<span className="text-foreground">
												24/7 Emergency Hotline
											</span>
										</div>
									</div>
								</div>
							</div>

							<div className="space-y-4">
								<div>
									<h4 className="font-medium text-foreground mb-2">
										Your Emergency Contact
									</h4>
									<div className="space-y-1 text-sm">
										{userProfile.emergency_contact_name && (
											<div className="flex items-center space-x-2">
												<Icon
													name="User"
													size={14}
													className="text-muted-foreground"
												/>
												<span className="text-foreground">
													{userProfile.emergency_contact_name}
												</span>
											</div>
										)}
										{userProfile.emergency_contact_phone && (
											<div className="flex items-center space-x-2">
												<Icon
													name="Phone"
													size={14}
													className="text-muted-foreground"
												/>
												<a
													href={`tel:${userProfile.emergency_contact_phone}`}
													className="text-foreground hover:text-primary"
												>
													{userProfile.emergency_contact_phone}
												</a>
											</div>
										)}
										{!userProfile.emergency_contact_name &&
											!userProfile.emergency_contact_phone && (
												<div className="text-muted-foreground text-xs">
													No emergency contact on file
												</div>
											)}
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="text-center py-4 text-muted-foreground">
							<p>Emergency contact information not available</p>
						</div>
					)}
				</div>
			</div>

			{/* Upload Signed Agreement Modal */}
			<UploadSignedAgreementModal
				lease={lease}
				isOpen={isUploadModalOpen}
				onClose={() => setIsUploadModalOpen(false)}
				onSuccess={() => {
					setIsUploadModalOpen(false);
					// Optionally refetch lease data here
				}}
			/>
		</div>
	);
};

export default LeaseDetails;
